import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, isSigningOut } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { IS_DEV, goToMain } from '../../lib/domain';

type OrgStatus = 'loading' | 'none' | 'auto' | 'ok';

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  const [orgStatus, setOrgStatus] = useState<OrgStatus>('loading');

  useEffect(() => {
    if (!session?.user) return;

    // Pas besoin de re-vérifier si on est déjà sur /onboarding
    if (location.pathname === '/onboarding') {
      setOrgStatus('ok');
      return;
    }

    const checkOrg = async () => {
      const { data } = await supabase
        .from('organization_members')
        .select('organizations(name)')
        .eq('user_id', session.user.id)
        .single();

      if (!data?.organizations) {
        setOrgStatus('none');
        return;
      }

      const org = data.organizations as { name: string };
      const fullName = session.user.user_metadata?.full_name as string | undefined;
      const hasRealName = !!fullName && !fullName.includes('@');

      if (org.name.endsWith("'s workspace") || !hasRealName) {
        setOrgStatus('auto');
      } else {
        setOrgStatus('ok');
      }
    };

    checkOrg();
  }, [session?.user?.id, location.pathname]);

  if (loading || (session && orgStatus === 'loading')) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    // Déconnexion volontaire : signOut redirige déjà vers la landing
    if (isSigningOut()) return null;

    // En prod sur app.revertai.io, redirect cross-domain vers revertai.io/login
    if (!IS_DEV) {
      goToMain('/login');
      return null;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (orgStatus === 'none' || orgStatus === 'auto') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
